import { computed, inject } from '@angular/core';
import { ColumnSetting, CtPreference, FilterPreference } from '@ct/model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { firstValueFrom, map, tap } from 'rxjs';
import { PreferencesService } from '../services/preferences-service';

// State interface
interface PreferencesState {
  columnSettings: ColumnSetting[];
  filterPreferences: { [id: string]: CtPreference };
  loading: boolean;
}

// Initial state
const initialState: PreferencesState = {
  columnSettings: [],
  filterPreferences: {},
  loading: false,
};

export const PreferencesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withComputed((store) => ({
    // Computed selector equivalent to getFilterPreferences
    filterPreferencesFormatted: computed(() => {
      const preferences = store.filterPreferences();
      let filterPreferences = {} as FilterPreference;
      Object.values(preferences).forEach((preference) => {
        if (preference) {
          filterPreferences = {
            ...filterPreferences,
            [preference.id]: { ...preference, default: preference.preSelected },
          };
        }
      });
      return filterPreferences;
    }),
  })),

  withMethods((store) => {
    const preferencesService = inject(PreferencesService);

    return {
      // Load preferences (equivalent to LoadPreferences action + effects)
      loadPreferences: async () => {
        patchState(store, { loading: true });

        // Load both requests independently so partial success is possible
        const [columnRes, preferenceRes] = await Promise.all([
          firstValueFrom(
            preferencesService.getColumnSettings().pipe(
              map((columnSettings) => {
                patchState(store, { columnSettings });
                return { success: true, data: columnSettings };
              }),
            ),
          ),
          firstValueFrom(
            preferencesService.getFilterPreferences().pipe(
              map((preferences) => {
                // Transform preferences array to object
                const filterPreferences =
                  preferences?.reduce((acc, preference) => ({ ...acc, [preference.id]: { ...preference } }), {}) || {};
                patchState(store, { filterPreferences });
                return { success: true, data: filterPreferences };
              }),
            ),
          ),
        ]);
        patchState(store, { loading: false });

        // Throw error only if both requests failed
        if (!columnRes.success && !preferenceRes.success) {
          throw new Error('Failed to load both column settings and filter preferences');
        }
      },

      // Update column settings (equivalent to updateColumnSettings action + effect)
      updateColumnSettings: async (columnSettings: ColumnSetting[]) => {
        try {
          // Update in service (fire and forget, like the original effect)
          preferencesService.setColumnSettings(columnSettings);
          // Update local state immediately
          patchState(store, { columnSettings });
        } catch (error) {
          console.error('Failed to update column settings:', error);
          throw error;
        }
      },

      // Edit filter preference (equivalent to editFilterPreference action + effect)
      editFilterPreference: async (preference: CtPreference) => {
        try {
          const editedPreference = await firstValueFrom(preferencesService.editFilterPreference(preference));

          patchState(store, (state) => ({
            filterPreferences: {
              ...state.filterPreferences,
              [editedPreference.id]: editedPreference,
            },
          }));
        } catch (error) {
          console.error('Failed to edit filter preference:', error);
          throw error;
        }
      },

      // Delete filter preference (equivalent to deleteFilterPreference action + effect)
      deleteFilterPreference: async (preference: CtPreference) => {
        try {
          await firstValueFrom(preferencesService.deleteFilterPreference(preference));

          patchState(store, (state) => ({
            filterPreferences: {
              ...state.filterPreferences,
              [preference.id]: null,
            },
          }));
        } catch (error) {
          console.error('Failed to delete filter preference:', error);
          throw error;
        }
      },

      // Default filter preference (equivalent to defaultFilterPreference action + effect)
      defaultFilterPreference: async (preference: CtPreference) => {
        try {
          await firstValueFrom(preferencesService.editFilterPreference(preference));

          patchState(store, (state) => ({
            filterPreferences: {
              ...state.filterPreferences,
              [preference.id]: { ...preference, preSelected: preference.default },
            },
          }));
        } catch (error) {
          console.error('Failed to set default filter preference:', error);
          throw error;
        }
      },

      // Direct state updates (for immediate UI feedback without server calls)
      setColumnSettings: (columnSettings: ColumnSetting[]) => {
        patchState(store, { columnSettings });
      },

      setFilterPreferences: (filterPreferences: { [id: string]: CtPreference }) => {
        patchState(store, { filterPreferences });
      },
    };
  }),
);
